import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { computeProjectMonthlyCost, formatUsd } from './projectCostEstimate';
import { getCostPanelTheme } from './storePanelTheme';

export default function ProjectCostSummary({
  catalog,
  entitlements,
  cart,
  locale,
  context = 'store',
  variant = 'full',
  themeMode = 'dark',
  onOpenStore,
}) {
  const [expanded, setExpanded] = useState(variant === 'full');
  const theme = getCostPanelTheme(context, themeMode);
  const estimate = useMemo(
    () => computeProjectMonthlyCost(catalog, entitlements, cart),
    [catalog, entitlements, cart],
  );

  if (!catalog?.entitlements) return null;

  const planCode = entitlements?.plan;
  const plan = catalog?.plans?.find((p) => p.code === planCode);
  const isCompact = variant === 'compact';
  const amountVariant = isCompact ? 'body1' : 'h5';

  const breakdown = (
    <>
      {estimate.lines.length > 0 ? (
        <Stack spacing={0.75} sx={{ pt: isCompact ? 0.5 : 0 }}>
          {estimate.lines.map((line) => (
            <Stack
              key={line.id}
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ gap: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: theme.lineLabelColor, fontSize: isCompact ? 13 : 14 }}>
                  {line.kind === 'plan' && locale.projectCostPlan}
                  {line.kind === 'addon' && locale.projectCostAddon}
                  {line.kind === 'quota' && locale.projectCostQuota}
                  {': '}
                  <Box component="span" sx={{ color: theme.amountColor }}>
                    {line.label}
                  </Box>
                </Typography>
                {line.detail && line.kind !== 'plan' ? (
                  <Typography variant="caption" sx={{ color: theme.subtitleColor }}>
                    {line.detail}
                  </Typography>
                ) : null}
              </Box>
              <Typography
                variant="body2"
                sx={{ color: theme.lineValueColor, fontWeight: 500, whiteSpace: 'nowrap', fontSize: isCompact ? 13 : 14 }}
              >
                {line.amount === 0
                  ? line.kind === 'plan'
                    ? locale.projectCostNoFee
                    : locale.includedInPlan
                  : formatUsd(line.amount, locale.perMonth)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}

      <Typography variant="caption" sx={{ color: theme.disclaimerColor, display: 'block', mt: 1.5 }}>
        {locale.projectCostDisclaimer}
      </Typography>
    </>
  );

  const headerRow = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: theme.titleColor }}>
          {locale.projectCostTitle}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant={amountVariant} sx={{ color: theme.amountColor, fontWeight: 600 }}>
            {isCompact
              ? formatUsd(estimate.total, locale.perMonth)
              : `${locale.projectCostApprox} ${formatUsd(estimate.total, locale.perMonth)}`}
          </Typography>
          {plan ? (
            <Typography variant="body2" sx={{ color: theme.subtitleColor }}>
              · {plan.name}
            </Typography>
          ) : null}
        </Stack>
        {!isCompact ? (
          <Typography variant="caption" sx={{ color: theme.subtitleColor, display: 'block', mt: 0.25 }}>
            {locale.projectCostSubtitle}
          </Typography>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {estimate.cartAdditional > 0 ? (
          <Typography variant="caption" sx={{ color: theme.subtitleColor }}>
            {locale.projectCostWithCart}: {formatUsd(estimate.projectedTotal, locale.perMonth)}
            {' '}(+{formatUsd(estimate.cartAdditional, locale.perMonth)})
          </Typography>
        ) : null}
        {onOpenStore ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<StorefrontIcon />}
            onClick={onOpenStore}
            sx={{
              color: theme.buttonColor,
              borderColor: theme.buttonBorder,
              '&:hover': {
                borderColor: theme.lineValueColor,
                bgcolor: 'rgba(255,255,255,0.04)',
              },
            }}
          >
            {locale.openStore}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );

  if (isCompact) {
    return (
      <Paper
        sx={{
          mb: 2,
          bgcolor: theme.paperBg,
          border: theme.border,
          overflow: 'hidden',
        }}
        elevation={0}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: estimate.lines.length ? 0 : 1.5 }}>{headerRow}</Box>
        {estimate.lines.length > 0 ? (
          <Accordion
            expanded={expanded}
            onChange={(_e, next) => setExpanded(next)}
            disableGutters
            elevation={0}
            sx={{
              bgcolor: 'transparent',
              '&:before': { display: 'none' },
              color: theme.lineLabelColor,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: theme.subtitleColor }} />}
              sx={{
                minHeight: 40,
                px: 2,
                '& .MuiAccordionSummary-content': { my: 0.5 },
              }}
            >
              <Typography variant="caption" sx={{ color: theme.subtitleColor }}>
                {expanded ? locale.projectCostCollapse : locale.projectCostExpand}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
              {breakdown}
            </AccordionDetails>
          </Accordion>
        ) : (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" sx={{ color: theme.disclaimerColor }}>
              {locale.projectCostDisclaimer}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        bgcolor: theme.paperBg,
        border: theme.border,
      }}
      elevation={0}
    >
      {headerRow}
      {estimate.lines.length > 0 ? (
        <>
          <Divider sx={{ my: 1.5, borderColor: theme.dividerColor }} />
          {breakdown}
        </>
      ) : (
        <Typography variant="caption" sx={{ color: theme.disclaimerColor, display: 'block', mt: 1.5 }}>
          {locale.projectCostDisclaimer}
        </Typography>
      )}
    </Paper>
  );
}

ProjectCostSummary.propTypes = {
  catalog: PropTypes.object,
  entitlements: PropTypes.object,
  cart: PropTypes.object,
  locale: PropTypes.object.isRequired,
  context: PropTypes.oneOf(['admin', 'store']),
  variant: PropTypes.oneOf(['compact', 'full']),
  themeMode: PropTypes.oneOf(['dark', 'light']),
  onOpenStore: PropTypes.func,
};
